import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  ChartNoAxesCombined,
  Check,
  ChevronDown,
  ClipboardCopy,
  Database,
  Languages,
  LayoutDashboard,
  LockKeyhole,
  Loader2,
  MapPinned,
  MessageSquareText,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Timer,
  Trash2,
  X,
} from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import './ReportsCopilotModal.css';

const EXAMPLE_QUESTIONS = [
  {
    question: 'Show all fires where the Security team responded more than five minutes late in the last six months.',
    label: 'Response',
    icon: Timer,
    iconClass: 'bg-[#FDEEE9] text-[#E4572E] ring-[#F8D7CC]',
    themeClass: 'reports-copilot-suggestion-icon--response',
    accentClass: 'from-[#E4572E]/10 via-transparent to-transparent',
  },
  {
    question: 'Which locations had the most high-severity incidents this year?',
    label: 'Locations',
    icon: MapPinned,
    iconClass: 'bg-[#E4F6F4] text-[#0EA5A0] ring-[#C7ECE8]',
    themeClass: 'reports-copilot-suggestion-icon--locations',
    accentClass: 'from-[#0EA5A0]/10 via-transparent to-transparent',
  },
  {
    question: 'Compare SLA compliance for real incidents versus drills this quarter.',
    label: 'SLA',
    icon: ShieldCheck,
    iconClass: 'bg-[#ECEEFE] text-[#4F46E5] ring-[#D9DCF9]',
    themeClass: 'reports-copilot-suggestion-icon--sla',
    accentClass: 'from-[#4F46E5]/10 via-transparent to-transparent',
  },
];

const QUICK_PROMPTS = [
  {
    label: 'Fires responded to over 5 min late — last 6 months',
    question: EXAMPLE_QUESTIONS[0].question,
  },
  {
    label: 'Top sites by high-severity incidents this year',
    question: EXAMPLE_QUESTIONS[1].question,
  },
  {
    label: 'SLA compliance: real incidents vs drills, Q3',
    question: EXAMPLE_QUESTIONS[2].question,
  },
];

const REPORTS_HISTORY_MAX_MESSAGES = 24;
const REPORTS_HISTORY_MAX_CHARACTERS = 30000;
const REPORTS_HISTORY_MAX_MESSAGE_CHARACTERS = 6000;
const REPORTS_COPILOT_UI_STORAGE_KEY = 'reportsCopilot.workspaceUi.v1';

const loadReportsCopilotUi = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) throw new Error('Browser storage unavailable');
    const value = JSON.parse(window.localStorage.getItem(REPORTS_COPILOT_UI_STORAGE_KEY) || '{}');
    return {
      language: value?.language === 'Arabic' ? 'Arabic' : 'English',
      usePageContext: Boolean(value?.usePageContext),
      sidebarCollapsed: Boolean(value?.sidebarCollapsed),
    };
  } catch (_storageError) {
    return { language: 'English', usePageContext: false, sidebarCollapsed: false };
  }
};

const saveReportsCopilotUi = (value) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(REPORTS_COPILOT_UI_STORAGE_KEY, JSON.stringify({
      language: value?.language === 'Arabic' ? 'Arabic' : 'English',
      usePageContext: Boolean(value?.usePageContext),
      sidebarCollapsed: Boolean(value?.sidebarCollapsed),
    }));
  } catch (_storageError) {
    // Conversation content is never copied into browser storage.
  }
};

const createConversationRecord = ({ language = 'English', usePageContext = false } = {}) => {
  const now = Date.now();
  return {
    id: `reports-conversation-${now}-${Math.random().toString(36).slice(2, 9)}`,
    title: 'New conversation',
    messages: [],
    sessionId: '',
    draft: '',
    language,
    usePageContext,
    createdAt: now,
    updatedAt: now,
    messagesLoaded: true,
  };
};

export const buildReportsConversationHistory = (messages) => {
  const candidates = asArray(messages)
    .filter((message) => (
      (message?.role === 'user' || message?.role === 'assistant')
      && String(message?.content || '').trim()
    ))
    .slice(-REPORTS_HISTORY_MAX_MESSAGES);
  let remainingCharacters = REPORTS_HISTORY_MAX_CHARACTERS;
  const history = [];
  for (let index = candidates.length - 1; index >= 0 && remainingCharacters > 0; index -= 1) {
    const message = candidates[index];
    const content = String(message.content || '').trim().slice(0, Math.min(
      REPORTS_HISTORY_MAX_MESSAGE_CHARACTERS,
      remainingCharacters,
    ));
    if (!content) continue;
    history.unshift({ role: message.role, content });
    remainingCharacters -= content.length;
  }
  return history;
};

const automaticConversationTitle = (value) => {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return 'New conversation';
  return normalized.length > 52 ? `${normalized.slice(0, 49).trimEnd()}…` : normalized;
};

const conversationTimestamp = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const ReportsConversationSidebar = ({
  open,
  collapsed,
  conversations,
  allConversationCount,
  activeId,
  search,
  renamingId,
  renameDraft,
  deleteConfirmId,
  historyStatus,
  onClose,
  onToggleCollapsed,
  onNew,
  onSearch,
  onSelect,
  onStartRename,
  onRenameDraft,
  onCommitRename,
  onCancelRename,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}) => {
  const [openMenuId, setOpenMenuId] = useState('');
  const openMenuRef = useRef(null);

  useEffect(() => {
    setOpenMenuId('');
  }, [activeId, deleteConfirmId, renamingId]);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const closeFromOutside = (event) => {
      if (!openMenuRef.current?.contains(event.target)) setOpenMenuId('');
    };
    const closeFromKeyboard = (event) => {
      if (event.key === 'Escape') setOpenMenuId('');
    };
    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromKeyboard);
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromKeyboard);
    };
  }, [openMenuId]);

  return (
    <>
    {open && (
      <button
        type="button"
        className="reports-copilot-sidebar-backdrop absolute inset-0 z-20 bg-[#131A33]/20 backdrop-blur-[1px] lg:hidden"
        aria-label="Close conversation history"
        onClick={onClose}
        data-testid="reports-copilot-sidebar-backdrop"
      />
    )}
    <aside
      aria-label="Reports Copilot conversation history"
      data-testid="reports-copilot-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={`reports-copilot-sidebar absolute inset-y-0 left-0 z-30 flex w-[min(84vw,300px)] flex-col bg-white shadow-[14px_0_38px_-28px_rgba(19,26,51,0.45)] transition-[width,transform] duration-200 lg:relative lg:z-auto lg:translate-x-0 lg:shadow-none ${open ? 'translate-x-0' : '-translate-x-full'} ${collapsed ? 'lg:w-[60px]' : 'lg:w-[300px]'}`}
    >
      <div className="flex flex-none items-start justify-between gap-2 px-4 pb-4 pt-5">
        <div className={`min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
          <p className="truncate text-[16px] font-bold leading-5 tracking-[-0.01em] text-[#131A33]">Conversations</p>
          <p className="mt-0.5 truncate text-[11px] font-medium text-[#9CA3AF]">Saved workspace</p>
        </div>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand conversation history' : 'Collapse conversation history'}
          title={collapsed ? 'Expand conversation history' : 'Collapse conversation history'}
          className="hidden h-8 w-8 flex-none items-center justify-center rounded-[9px] border border-[#EAECF3] bg-white text-[#9CA3AF] transition hover:border-[#C7CBEA] hover:text-[#6B7280] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/30 lg:flex"
          data-testid="reports-copilot-sidebar-collapse"
        >
          {collapsed ? <PanelLeftOpen className="h-[17px] w-[17px]" /> : <PanelLeftClose className="h-[17px] w-[17px]" />}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close conversation history"
          className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] border border-[#EAECF3] bg-white text-[#9CA3AF] transition hover:border-[#C7CBEA] hover:text-[#6B7280] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/30 lg:hidden"
        >
          <X className="h-[17px] w-[17px]" />
        </button>
      </div>

      <div className="flex flex-none justify-center px-4">
        <button
          type="button"
          onClick={onNew}
          className={`reports-copilot-primary reports-copilot-new-conversation inline-flex h-11 items-center justify-center gap-2 rounded-[12px] bg-[#0B1220] text-[13px] font-semibold text-white shadow-[0_8px_20px_-10px_rgba(11,18,32,0.7)] transition hover:bg-[#1B2437] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/35 focus-visible:ring-offset-2 ${collapsed ? 'lg:h-10 lg:w-10 lg:px-0' : 'w-full px-3'}`}
          aria-label="Start a new conversation"
          title="New conversation"
          data-testid="reports-copilot-sidebar-new"
        >
          <Plus className="h-[17px] w-[17px] flex-none" strokeWidth={2.4} />
          <span className={collapsed ? 'lg:sr-only' : ''}>New conversation</span>
        </button>
      </div>

      <div className={`flex min-h-0 flex-1 flex-col ${collapsed ? 'lg:hidden' : ''}`}>
        <div className="mt-3 px-4">
          <label className="relative block">
            <span className="sr-only">Search conversations</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="search"
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              placeholder="Search conversations"
              className="h-10 w-full rounded-[11px] border border-[#EDEEF4] bg-[#F7F8FC] pl-9 pr-3 text-[12.5px] text-[#131A33] outline-none transition placeholder:text-[#9CA3AF] focus:border-[#C7CBEA] focus:bg-white focus:ring-2 focus:ring-[#4F46E5]/10"
              data-testid="reports-copilot-sidebar-search"
            />
          </label>
        </div>

        <div
          className={`reports-copilot-sidebar-list mt-4 min-h-0 flex-1 px-4 ${conversations.length === 0 ? 'flex' : 'overflow-y-auto overscroll-contain'}`}
          data-testid="reports-copilot-sidebar-list"
        >
          {conversations.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-[13px] border border-dashed border-[#E1E3ED] px-4 py-6 text-center" data-testid="reports-copilot-sidebar-empty">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#F1F2F8] text-[#B4B9CC]">
                <MessageSquareText className="h-[18px] w-[18px]" strokeWidth={1.9} />
              </span>
              <p className="mt-3 text-[13px] font-bold text-[#4B5468]">
                {allConversationCount ? 'No matching conversations' : 'No conversations yet'}
              </p>
              <p className="mt-1 text-[11px] leading-[1.4] text-[#9CA3AF]">
                {allConversationCount ? 'Try a different search.' : 'Your first question will appear here.'}
              </p>
            </div>
          ) : conversations.map((conversation) => {
            const active = conversation.id === activeId;
            const renaming = conversation.id === renamingId;
            const confirmingDelete = conversation.id === deleteConfirmId;
            const menuOpen = conversation.id === openMenuId;
            return (
              <div
                key={conversation.id}
                className={`group relative mb-1.5 rounded-[12px] border transition ${active
                  ? 'reports-copilot-sidebar-item--active border-[#D9DCF9] bg-[#ECEEFE] shadow-[0_1px_3px_rgba(79,70,229,0.08)]'
                  : 'border-transparent hover:border-[#E7E9F2] hover:bg-[#F8FAFB]'}`}
                data-testid="reports-copilot-sidebar-item"
                data-conversation-id={conversation.id}
              >
                {renaming ? (
                  <div className="p-2">
                    <label className="sr-only" htmlFor={`reports-conversation-rename-${conversation.id}`}>Rename conversation</label>
                    <input
                      id={`reports-conversation-rename-${conversation.id}`}
                      value={renameDraft}
                      onChange={(event) => onRenameDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          onCommitRename(conversation.id);
                        }
                        if (event.key === 'Escape') onCancelRename();
                      }}
                      className="h-9 w-full rounded-[9px] border border-[#C7CBEA] bg-white px-2.5 text-[12px] font-semibold text-[#131A33] outline-none focus:border-[#4F46E5]"
                      data-testid="reports-copilot-sidebar-rename-input"
                    />
                    <div className="mt-1.5 flex justify-end gap-1.5">
                      <button type="button" onClick={onCancelRename} className="rounded-md px-2 py-1 text-[10px] font-semibold text-[#8A90A6] hover:bg-white">Cancel</button>
                      <button type="button" onClick={() => onCommitRename(conversation.id)} className="rounded-md bg-[#131A33] px-2 py-1 text-[10px] font-bold text-white" data-testid="reports-copilot-sidebar-rename-save">Save</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => onSelect(conversation.id)}
                      aria-current={active ? 'true' : undefined}
                      className="block w-full min-w-0 rounded-[11px] py-2.5 pl-3 pr-[62px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4F46E5]/40"
                      data-testid="reports-copilot-sidebar-select"
                    >
                      <span className="block truncate text-[12px] font-semibold text-[#131A33]">{conversation.title}</span>
                      <span className="mt-1 block truncate text-[9.5px] font-medium text-[#9AA0B4]">{conversationTimestamp(conversation.updatedAt)}</span>
                    </button>
                    {confirmingDelete ? (
                      <div className="reports-copilot-delete-confirmation absolute right-2 top-2 flex items-center gap-1 rounded-lg bg-white p-1 shadow-sm ring-1 ring-red-100">
                        <button type="button" onClick={onCancelDelete} className="rounded px-1.5 py-1 text-[9px] font-bold text-[#8A90A6] hover:bg-[#F1F2F8]">Cancel</button>
                        <button type="button" onClick={() => onConfirmDelete(conversation.id)} className="rounded bg-red-600 px-1.5 py-1 text-[9px] font-bold text-white hover:bg-red-700" data-testid="reports-copilot-sidebar-delete-confirm">Delete</button>
                      </div>
                    ) : (
                      <div
                        ref={menuOpen ? openMenuRef : undefined}
                        className={`reports-copilot-row-menu absolute right-2 top-2 z-10 ${active || menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'}`}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenMenuId((current) => (
                            current === conversation.id ? '' : conversation.id
                          ))}
                          className="reports-copilot-row-menu-trigger flex h-8 w-8 items-center justify-center rounded-[9px] text-[#8A90A6] transition hover:bg-[#F1F2F8] hover:text-[#131A33] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/30"
                          aria-label={`Conversation actions for ${conversation.title}`}
                          aria-haspopup="menu"
                          aria-expanded={menuOpen ? 'true' : 'false'}
                          aria-controls={`reports-conversation-menu-${conversation.id}`}
                          title="Conversation actions"
                          data-testid="reports-copilot-sidebar-menu-trigger"
                        >
                          <MoreHorizontal className="h-[17px] w-[17px]" strokeWidth={2.1} />
                        </button>
                        {menuOpen && (
                          <div
                            id={`reports-conversation-menu-${conversation.id}`}
                            role="menu"
                            aria-label={`Actions for ${conversation.title}`}
                            className="reports-copilot-row-menu-popover absolute right-0 top-[calc(100%+6px)] z-20 w-[142px] overflow-hidden rounded-[11px] p-1.5"
                            data-testid="reports-copilot-sidebar-menu"
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setOpenMenuId('');
                                onStartRename(conversation);
                              }}
                              className="reports-copilot-row-menu-item flex w-full items-center gap-2 rounded-[8px] px-2.5 py-2 text-left text-[11px] font-semibold"
                              data-testid="reports-copilot-sidebar-rename"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Rename
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setOpenMenuId('');
                                onRequestDelete(conversation.id);
                              }}
                              className="reports-copilot-row-menu-item reports-copilot-row-menu-item--danger mt-0.5 flex w-full items-center gap-2 rounded-[8px] px-2.5 py-2 text-left text-[11px] font-semibold"
                              data-testid="reports-copilot-sidebar-delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <p className="flex-none px-4 pb-5 pt-4 text-center text-[10.5px] font-medium leading-4 text-[#B4B9CC]">
          {historyStatus === 'loading'
            ? 'Loading saved conversations…'
            : historyStatus === 'synced'
              ? 'Saved to your secure reporting workspace.'
              : 'Saved history is temporarily unavailable.'}
        </p>
      </div>
    </aside>
    </>
  );
};

const SCOPE_LABELS = {
  all: 'All records',
  real: 'Real incidents',
  drill: 'Exercises & drills',
  sos: 'SOS-origin',
};

const TAB_LABELS = {
  overview: 'Overview',
  register: 'Incident & Exercise Register',
  report: 'Response Report',
  perf: 'SLA & Performance',
  adherence: 'Scenarios & Program',
  ops: 'Operations',
  acks: 'Acknowledgment Audit',
  ca: 'Corrective Actions',
  audit: 'Audit Log',
  notifications: 'Notification Log',
};

const FILTER_LABELS = {
  from_time: 'From',
  to_time: 'To',
  incident_type: 'Type',
  event: 'Event',
  trigger: 'Trigger',
  location: 'Location',
  severity: 'Severity',
  status: 'Status',
  status_mode: 'Status',
  q: 'Search',
};

export const ReportsCopilotMark = ({ size = 'md', className = '' }) => {
  const sizeClass = size === 'lg'
    ? 'h-16 w-16 rounded-2xl'
    : size === 'sm'
      ? 'h-8 w-8 rounded-lg'
      : size === 'xs'
        ? 'h-5 w-5 rounded-md'
        : 'h-10 w-10 rounded-xl';
  const chartClass = size === 'lg' ? 'h-8 w-8' : size === 'xs' ? 'h-3 w-3' : 'h-5 w-5';
  const sparkleClass = size === 'lg'
    ? 'h-5 w-5 -right-1.5 -top-1.5'
    : size === 'xs'
      ? 'h-2.5 w-2.5 -right-1 -top-1'
      : 'h-3.5 w-3.5 -right-1 -top-1';
  return (
    <span
      aria-hidden="true"
      data-testid="reports-copilot-mark"
      className={`relative inline-flex flex-none items-center justify-center bg-white/10 text-white shadow-[0_8px_24px_rgba(79,70,229,0.32)] ring-1 ring-white/40 ${sizeClass} ${className}`}
    >
      <ChartNoAxesCombined className={chartClass} strokeWidth={2.2} />
      <span className={`absolute inline-flex items-center justify-center rounded-full bg-white p-0.5 text-indigo-700 shadow-sm ring-1 ring-indigo-100 ${sparkleClass}`}>
        <Sparkles className="h-full w-full" strokeWidth={2.4} />
      </span>
    </span>
  );
};

const ReportsLanguageSelect = ({ value, onValueChange, variant = 'header' }) => (
  <div className="flex items-center">
    <label htmlFor="reports-copilot-language" className="sr-only">Response language</label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        id="reports-copilot-language"
        aria-label="Response language"
        title={`Response language: ${value}`}
        className={variant === 'landing'
          ? 'flex h-10 w-10 items-center justify-center rounded-[11px] border border-[#E2E4EE] bg-white p-0 text-[#4B5468] shadow-none transition hover:border-[#C7CBEA] hover:text-[#131A33] focus:ring-2 focus:ring-[#4F46E5]/30 [&>svg]:hidden'
          : 'h-10 w-auto min-w-[126px] gap-2 rounded-xl border-white/15 bg-white/10 px-3 text-xs font-bold text-white shadow-none backdrop-blur-md transition hover:border-white/25 hover:bg-white/15 focus:ring-2 focus:ring-cyan-300 [&>svg]:text-cyan-100 [&>svg]:opacity-100'}
        data-testid="reports-copilot-language"
      >
        {variant === 'landing' ? (
          <span className="inline-flex items-center justify-center"><Languages className="h-[17px] w-[17px]" strokeWidth={2} /></span>
        ) : (
          <span className="inline-flex min-w-0 items-center gap-2">
            <Languages className="h-4 w-4 flex-none text-cyan-200" />
            <span className="min-w-0 text-left leading-none">
              <span className="mb-1 hidden text-[8px] font-black uppercase tracking-[0.16em] text-cyan-100/55 lg:block">Response language</span>
              <span className="block truncate"><SelectValue>{value === 'Arabic' ? 'العربية' : 'English'}</SelectValue></span>
            </span>
          </span>
        )}
      </SelectTrigger>
      <SelectContent className="reports-copilot-language-menu z-[1200] min-w-[150px] border-slate-200 bg-white text-slate-900 shadow-xl">
        <SelectItem value="English" textValue="English">
          <span className="inline-flex items-center gap-2"><span className="w-5 text-[10px] font-black text-indigo-600">EN</span>English</span>
        </SelectItem>
        <SelectItem value="Arabic" textValue="العربية">
          <span className="inline-flex items-center gap-2"><span className="w-5 text-[10px] font-black text-indigo-600">AR</span><span dir="rtl">العربية</span></span>
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const responseError = (error, fallback) => (
  error?.response?.data?.detail
  || error?.response?.data?.message
  || error?.data?.detail
  || error?.data?.message
  || error?.message
  || fallback
);

const responseFailed = (response) => {
  let current = response;
  const seen = new Set();
  for (let depth = 0; current && typeof current === 'object' && depth < 5; depth += 1) {
    if (seen.has(current)) break;
    seen.add(current);
    if (current.status === false || current.success === false) return true;
    current = current.data;
  }
  return false;
};

export const reportsAssistantPayload = (response) => {
  let current = response;
  const seen = new Set();
  for (let depth = 0; current && typeof current === 'object' && depth < 6; depth += 1) {
    if (seen.has(current)) break;
    seen.add(current);
    if (
      typeof current.answer === 'string'
      || current.answer_mode
      || current.answerMode
      || current.response_mode
      || current.responseMode
    ) return current;
    current = current.data;
  }
  return null;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  return [value];
};

const responseDataCandidates = (response) => {
  const values = [];
  let current = response;
  const seen = new Set();
  for (let depth = 0; current && typeof current === 'object' && depth < 7; depth += 1) {
    if (seen.has(current)) break;
    seen.add(current);
    values.push(current);
    current = current.data;
  }
  return values;
};

const parseServerTimestamp = (value, fallback) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const normalizeServerMessage = (message, index) => {
  const role = message?.role === 'assistant' ? 'assistant' : message?.role === 'user' ? 'user' : null;
  const content = String(message?.content || message?.text || message?.answer || message?.question || '').trim();
  if (!role || !content) return null;
  return {
    id: String(message?.id || `${role}-server-${index}-${message?.created_at || message?.createdAt || ''}`),
    role,
    content,
    language: message?.language || 'English',
    ...(role === 'assistant'
      ? { payload: message?.payload || message?.response_payload || message?.responsePayload || {} }
      : {}),
  };
};

const normalizeServerConversation = (conversation, fallback = {}) => {
  if (!conversation || typeof conversation !== 'object') return null;
  const sessionId = String(
    conversation.session_id
    || conversation.sessionId
    || conversation.conversation_id
    || conversation.conversationId
    || conversation.id
    || '',
  ).trim();
  if (!sessionId) return null;
  const rawMessages = conversation.messages || conversation.turns;
  const hasMessages = Array.isArray(rawMessages);
  const messageCount = Number(conversation.message_count ?? conversation.messageCount ?? rawMessages?.length ?? 0);
  const messagesLoaded = hasMessages && (rawMessages.length > 0 || messageCount === 0);
  const now = Date.now();
  return {
    id: sessionId,
    title: automaticConversationTitle(conversation.title || fallback.title || 'New conversation'),
    messages: hasMessages
      ? rawMessages.map(normalizeServerMessage).filter(Boolean)
      : asArray(fallback.messages),
    sessionId,
    draft: fallback.draft || '',
    language: conversation.language || fallback.language || 'English',
    usePageContext: Boolean(conversation.use_page_context ?? conversation.usePageContext ?? fallback.usePageContext),
    createdAt: parseServerTimestamp(
      conversation.created_at || conversation.createdAt,
      fallback.createdAt || now,
    ),
    updatedAt: parseServerTimestamp(
      conversation.updated_at || conversation.updatedAt,
      fallback.updatedAt || now,
    ),
    messagesLoaded,
    messageCount,
  };
};

const reportsConversationList = (response) => {
  for (const candidate of responseDataCandidates(response)) {
    if (Array.isArray(candidate)) return candidate;
    const values = candidate.conversations || candidate.items || candidate.results;
    if (Array.isArray(values)) return values;
  }
  return [];
};

const reportsConversationRecord = (response) => {
  for (const candidate of responseDataCandidates(response)) {
    const nested = candidate.conversation || candidate.item || candidate.result;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested;
    if (
      candidate.id
      || candidate.session_id
      || candidate.sessionId
      || candidate.conversation_id
      || candidate.conversationId
    ) return candidate;
  }
  return null;
};

const displayValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (Array.isArray(value)) return value.map(displayValue).join(', ');
  if (typeof value === 'object') {
    const from = value.from || value.start || value.from_time;
    const to = value.to || value.end || value.to_time;
    if (from || to) return `${from || 'Beginning'} → ${to || 'Now'}`;
    return Object.entries(value).map(([key, item]) => `${key.replace(/_/g, ' ')}: ${displayValue(item)}`).join(' · ');
  }
  return String(value);
};

const normalizedColumns = (resultSet) => {
  const rows = Array.isArray(resultSet?.rows) ? resultSet.rows : [];
  const supplied = Array.isArray(resultSet?.columns) ? resultSet.columns : [];
  if (supplied.length) {
    return supplied.map((column) => (typeof column === 'string'
      ? { key: column, label: column.replace(/_/g, ' ') }
      : { key: column?.key || column?.field || column?.label, label: column?.label || column?.key || column?.field }));
  }
  const firstObjectRow = rows.find((row) => row && typeof row === 'object' && !Array.isArray(row));
  return firstObjectRow
    ? Object.keys(firstObjectRow).map((key) => ({ key, label: key.replace(/_/g, ' ') }))
    : [];
};

const metricEntries = (metrics) => {
  if (Array.isArray(metrics)) {
    return metrics.map((metric, index) => ({
      key: metric?.key || metric?.label || `metric-${index}`,
      label: metric?.label || metric?.key || `Metric ${index + 1}`,
      value: metric?.value ?? metric?.count ?? metric,
      unit: metric?.unit || '',
    }));
  }
  if (!metrics || typeof metrics !== 'object') return [];
  return Object.entries(metrics).map(([key, metric]) => {
    const structured = metric && typeof metric === 'object' && !Array.isArray(metric) && Object.prototype.hasOwnProperty.call(metric, 'value');
    return {
      key,
      label: structured ? (metric.label || key.replace(/_/g, ' ')) : key.replace(/_/g, ' '),
      value: structured ? metric.value : metric,
      unit: structured ? (metric.unit || '') : '',
    };
  });
};

const displayMetricValue = (value, unit) => {
  const displayed = displayValue(value);
  const normalizedUnit = String(unit || '').trim();
  if (!normalizedUnit || displayed === '—') return displayed;
  if (displayed.toLowerCase().endsWith(normalizedUnit.toLowerCase())) return displayed;
  return ['%', '°', '°C', '°F'].includes(normalizedUnit)
    ? `${displayed}${normalizedUnit}`
    : `${displayed} ${normalizedUnit}`;
};

const safeEvidenceHref = (item) => {
  const direct = String(item?.url || '').trim();
  if (/^\/(?!\/)(?![^\s]*\\)[A-Za-z0-9._~!$&'()*+,;=:@%/?-]*$/.test(direct)) return direct;
  const incidentId = item?.incident_id || item?.incidentId;
  return incidentId ? `/incident-details/${encodeURIComponent(incidentId)}` : '';
};

const safeMarkdownHref = (href) => {
  const value = String(href || '').trim();
  return /^\/(?!\/)(?![^\s]*\\)[A-Za-z0-9._~!$&'()*+,;=:@%/?-]*$/.test(value) ? value : '';
};

const REPORTS_MARKDOWN_COMPONENTS = {
  // Answers are data, not a remote-content surface. Blocking images prevents
  // tracking pixels; links are restricted to this application.
  img: () => null,
  a: ({ href, children }) => {
    const safeHref = safeMarkdownHref(href);
    return safeHref ? <a href={safeHref}>{children}</a> : <span>{children}</span>;
  },
};

const resultCellHref = (column, row, value) => {
  const key = String(column?.key || '').toLowerCase();
  if (!/(^id$|incident.*id|record.*id)/.test(key)) return '';
  const incidentId = row?.incident_id || row?.incidentId || value;
  return String(incidentId || '').trim()
    ? `/incident-details/${encodeURIComponent(String(incidentId).trim())}`
    : '';
};

const cleanPageContext = (pageContext) => {
  if (!pageContext || typeof pageContext !== 'object') return null;
  const filters = Object.fromEntries(Object.entries(pageContext.filters || {}).filter(([, value]) => (
    value != null && value !== '' && value !== 'all'
  )));
  return {
    scope: pageContext.scope || 'all',
    tab: pageContext.tab || 'overview',
    filters,
  };
};

const pageContextSummary = (pageContext) => {
  const clean = cleanPageContext(pageContext);
  if (!clean) return '';
  const filterText = Object.entries(clean.filters).map(([key, value]) => (
    `${FILTER_LABELS[key] || key.replace(/_/g, ' ')}: ${displayValue(value)}`
  ));
  return [
    SCOPE_LABELS[clean.scope] || clean.scope,
    TAB_LABELS[clean.tab] || clean.tab,
    ...filterText,
  ].join(' · ');
};

const Interpretation = ({ payload }) => {
  const interpreted = payload?.interpreted_query || payload?.interpretedQuery;
  const asOf = payload?.as_of || payload?.asOf || interpreted?.as_of || interpreted?.asOf || payload?.generated_at || payload?.generatedAt;
  if (!interpreted && !asOf) return null;
  const fields = [
    ['Scope', interpreted?.scope],
    ['Date range', interpreted?.date_range || interpreted?.dateRange],
    ['Metric definition', interpreted?.metric_definition || interpreted?.metricDefinition],
    ['As of', asOf ? new Date(asOf).toString() === 'Invalid Date' ? asOf : new Date(asOf).toLocaleString() : null],
  ].filter(([, value]) => value != null && value !== '');
  return (
    <section className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3" data-testid="reports-copilot-interpretation">
      <p className="text-[10px] font-black uppercase tracking-[0.13em] text-indigo-700">How I interpreted the question</p>
      {interpreted?.summary && <p className="mt-1.5 text-xs font-semibold leading-relaxed text-slate-800">{interpreted.summary}</p>}
      {fields.length > 0 && (
        <dl className="mt-2 grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
          {fields.map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="font-bold text-slate-500">{label}</dt>
              <dd className="break-words text-slate-800">{displayValue(value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {interpreted?.filters && Object.keys(interpreted.filters).length > 0 && (
        <p className="mt-2 text-xs text-slate-600"><span className="font-bold">Filters:</span> {displayValue(interpreted.filters)}</p>
      )}
    </section>
  );
};

const StructuredResults = ({ payload }) => {
  const metrics = metricEntries(payload?.metrics);
  const resultSet = payload?.result_set || payload?.resultSet;
  const rows = Array.isArray(resultSet?.rows) ? resultSet.rows : [];
  const columns = normalizedColumns(resultSet);
  if (!metrics.length && !rows.length && resultSet?.total == null) return null;
  return (
    <section className="mt-3 space-y-3" data-testid="reports-copilot-structured-results">
      {metrics.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="reports-copilot-metrics">
          {metrics.map((metric) => (
            <div key={metric.key} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-base font-black text-slate-900">{displayMetricValue(metric.value, metric.unit)}</p>
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{metric.label}</p>
            </div>
          ))}
        </div>
      )}
      {(rows.length > 0 || resultSet?.total != null) && (
        <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="reports-copilot-results">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
            <span className="font-bold text-slate-700">Results</span>
            <span>{resultSet?.returned ?? rows.length} shown{resultSet?.total != null ? ` · ${resultSet.total} total` : ''}</span>
          </div>
          {rows.length > 0 && columns.length > 0 && (
            <div className="max-h-64 overflow-auto">
              <table className="w-full min-w-[520px] text-left text-xs">
                <thead className="sticky top-0 bg-white text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>{columns.map((column) => <th key={column.key} className="border-b border-slate-100 px-3 py-2 font-bold">{column.label}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={`${row?.id || row?.incident_id || row?.incidentId || 'result'}-${rowIndex}`} className="border-b border-slate-50 last:border-0" data-testid="reports-copilot-result-row">
                      {columns.map((column, columnIndex) => {
                        const value = Array.isArray(row) ? row[columnIndex] : row?.[column.key];
                        const href = resultCellHref(column, row, value);
                        return (
                          <td key={`${column.key}-${columnIndex}`} className="max-w-[280px] px-3 py-2 align-top text-slate-700">
                            {href ? <a href={href} className="font-bold text-indigo-700 hover:underline">{displayValue(value)}</a> : <span className="break-words">{displayValue(value)}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const Evidence = ({ evidence }) => {
  const items = asArray(evidence).filter(Boolean);
  if (!items.length) return null;
  return (
    <details className="mt-3 rounded-lg border border-cyan-100 bg-cyan-50/50" data-testid="reports-copilot-evidence">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs font-bold text-cyan-950 [&::-webkit-details-marker]:hidden">
        <ShieldCheck className="h-3.5 w-3.5" /> Evidence ({items.length}) <ChevronDown className="ml-auto h-3.5 w-3.5" />
      </summary>
      <ul className="space-y-1.5 border-t border-cyan-100 px-3 py-2.5 text-xs text-cyan-950/80">
        {items.map((item, index) => {
          const value = typeof item === 'string' ? { label: item } : item;
          const href = safeEvidenceHref(value);
          const label = value?.label || value?.title || value?.id || value?.incident_id || `Evidence ${index + 1}`;
          return (
            <li key={value?.id || `${label}-${index}`} className="flex items-start gap-2">
              <span aria-hidden="true">•</span>
              <span className="min-w-0">
                {href ? <a href={href} className="font-semibold text-cyan-900 hover:underline">{label}</a> : <span className="font-semibold">{label}</span>}
                {value?.timestamp && <span className="ml-1 text-cyan-900/50">· {new Date(value.timestamp).toLocaleString()}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </details>
  );
};

const answerPresentation = (payload) => {
  const rawMode = String(
    payload?.answer_mode
    || payload?.answerMode
    || payload?.response_mode
    || payload?.responseMode
    || payload?.mode
    || payload?.grounding?.mode
    || payload?.grounding
    || '',
  ).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (/(refus|denied|unsafe|not_authorized)/.test(rawMode)) {
    return {
      mode: 'refusal',
      label: 'Unable to answer safely',
      detail: 'No unsupported or unauthorized result was generated.',
      Icon: AlertCircle,
      className: 'border-red-200 bg-red-50 text-red-700',
    };
  }
  if (/(unavailable|no_data|data_missing|error)/.test(rawMode)) {
    return {
      mode: 'unavailable',
      label: 'Authorized data unavailable',
      detail: 'The answer identifies where the required evidence is unavailable.',
      Icon: AlertTriangle,
      className: 'border-amber-200 bg-amber-50 text-amber-800',
    };
  }
  if (/(clarif|follow_up|required_context)/.test(rawMode)) {
    return {
      mode: 'clarification',
      label: 'Clarification needed',
      detail: 'More detail is needed before a reliable calculation can be made.',
      Icon: MessageSquareText,
      className: 'border-amber-200 bg-amber-50 text-amber-800',
    };
  }
  if (/(ai|synth|generat|narrative)/.test(rawMode) || payload?.ai_synthesized === true || payload?.aiSynthesized === true) {
    return {
      mode: 'ai_synthesis',
      label: 'AI-organized grounded answer',
      detail: 'AI selected relevant server-calculated facts; linked authorized records remain the source of truth.',
      Icon: Sparkles,
      className: 'border-indigo-200 bg-indigo-50 text-indigo-800',
    };
  }
  if (/(calculat|deterministic|grounded|authoritative)/.test(rawMode)) {
    return {
      mode: 'grounded_calculation',
      label: 'Grounded calculation',
      detail: 'Calculated directly from authorized records using the shown method and evidence.',
      Icon: ShieldCheck,
      className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    };
  }
  return {
    mode: 'authorized_answer',
    label: 'Authorized report answer',
    detail: 'Review the shown methodology and linked evidence before using this answer.',
    Icon: ShieldCheck,
    className: 'border-slate-200 bg-slate-50 text-slate-700',
  };
};

const AssistantMessage = ({ message, onCopy, copied, onFollowUp }) => {
  const payload = message.payload || {};
  const warnings = asArray(payload.warnings).filter(Boolean);
  const suggestions = asArray(payload.follow_up_suggestions || payload.followUpSuggestions).filter(Boolean);
  const presentation = answerPresentation(payload);
  const AnswerModeIcon = presentation.Icon;
  const groundingDetail = String(payload?.grounding?.note || presentation.detail).trim();
  return (
    <article className="max-w-[94%] self-start" data-testid="reports-copilot-message-assistant" dir={message.language === 'Arabic' ? 'rtl' : 'ltr'}>
      <div className="rounded-2xl rounded-tl-sm border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div
          className={`mb-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold ${presentation.className}`}
          data-testid="reports-copilot-answer-mode"
          data-mode={presentation.mode}
          title={groundingDetail}
        >
          <AnswerModeIcon className="h-3 w-3" />
          {presentation.label}
        </div>
        <div className="prose prose-sm prose-slate max-w-none prose-headings:font-bold prose-a:text-indigo-700" data-testid="reports-copilot-answer">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={REPORTS_MARKDOWN_COMPONENTS}>{message.content}</ReactMarkdown>
        </div>
        <Interpretation payload={payload} />
        <StructuredResults payload={payload} />
        {warnings.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="reports-copilot-warnings">
            <p className="flex items-center gap-1.5 font-bold"><AlertTriangle className="h-3.5 w-3.5" /> Important limits</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-4">{warnings.map((warning, index) => <li key={`${displayValue(warning)}-${index}`}>{displayValue(warning)}</li>)}</ul>
          </div>
        )}
        <Evidence evidence={payload.evidence} />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><AnswerModeIcon className="h-3 w-3" /> {groundingDetail}</span>
          <button type="button" onClick={() => onCopy(message)} className="inline-flex items-center gap-1 font-bold text-slate-500 hover:text-indigo-700" data-testid={`reports-copilot-copy-${message.id}`}>
            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <ClipboardCopy className="h-3 w-3" />}{copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      {suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="reports-copilot-follow-ups">
          {suggestions.map((suggestion, index) => {
            const text = typeof suggestion === 'string' ? suggestion : suggestion?.label || suggestion?.question;
            return text ? <button key={`${text}-${index}`} type="button" onClick={() => onFollowUp(text)} className="rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-left text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50">{text}</button> : null;
          })}
        </div>
      )}
    </article>
  );
};

const ReportsCopilotModal = ({ onClose, pageContext = null, embedded = false }) => {
  const initialUiRef = useRef(null);
  if (!initialUiRef.current) initialUiRef.current = loadReportsCopilotUi();
  const initialConversationRef = useRef(null);
  if (!initialConversationRef.current) {
    initialConversationRef.current = createConversationRecord({
      language: initialUiRef.current.language,
      usePageContext: Boolean(initialUiRef.current.usePageContext && pageContext),
    });
  }
  const [conversations, setConversations] = useState(() => [initialConversationRef.current]);
  const [activeConversationId, setActiveConversationId] = useState(initialConversationRef.current.id);
  const [language, setLanguage] = useState(initialUiRef.current.language);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState('');
  const [usePageContext, setUsePageContext] = useState(Boolean(initialUiRef.current.usePageContext && pageContext));
  const [conversationSearch, setConversationSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialUiRef.current.sidebarCollapsed);
  const [renamingConversationId, setRenamingConversationId] = useState('');
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState('');
  const [historyStatus, setHistoryStatus] = useState('loading');
  const requestRef = useRef(0);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const messagesRef = useRef([]);
  const sessionRef = useRef('');
  const conversationsRef = useRef([initialConversationRef.current]);
  const activeConversationIdRef = useRef(initialConversationRef.current.id);
  const onCloseRef = useRef(onClose);
  const inputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const cleanContext = useMemo(() => cleanPageContext(pageContext), [pageContext]);
  const contextSummary = useMemo(() => pageContextSummary(pageContext), [pageContext]);
  const pageContextDetails = useMemo(() => {
    if (!cleanContext) return [];
    return [
      {
        key: 'scope',
        label: 'Scope',
        value: SCOPE_LABELS[cleanContext.scope] || cleanContext.scope,
      },
      {
        key: 'tab',
        label: 'Report view',
        value: TAB_LABELS[cleanContext.tab] || cleanContext.tab,
      },
      ...Object.entries(cleanContext.filters).map(([key, value]) => ({
        key: `filter-${key}`,
        label: FILTER_LABELS[key] || key.replace(/_/g, ' '),
        value: displayValue(value),
      })),
    ];
  }, [cleanContext]);
  const hasPageContextFilters = Boolean(cleanContext && Object.keys(cleanContext.filters).length);
  const isArabic = language === 'Arabic';

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    saveReportsCopilotUi({ language, usePageContext, sidebarCollapsed });
  }, [language, sidebarCollapsed, usePageContext]);

  const commitConversations = (updater) => {
    const next = typeof updater === 'function' ? updater(conversationsRef.current) : updater;
    conversationsRef.current = next;
    setConversations(next);
    return next;
  };

  const updateActiveConversation = (updates) => {
    const activeId = activeConversationIdRef.current;
    commitConversations((current) => current.map((conversation) => (
      conversation.id === activeId
        ? { ...conversation, ...updates }
        : conversation
    )));
  };

  const replaceMessages = (next) => {
    messagesRef.current = next;
    setMessages(next);
    const firstUserMessage = next.find((message) => message.role === 'user')?.content;
    const active = conversationsRef.current.find((conversation) => conversation.id === activeConversationIdRef.current);
    updateActiveConversation({
      messages: next,
      updatedAt: Date.now(),
      ...(active?.title === 'New conversation' && firstUserMessage
        ? { title: automaticConversationTitle(firstUserMessage) }
        : {}),
    });
  };

  useEffect(() => {
    mountedRef.current = true;
    const previouslyFocused = document.activeElement;
    const closeOnEscape = (event) => {
      if (!embedded && event.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      document.removeEventListener('keydown', closeOnEscape);
      if (!embedded && previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, [embedded]);

  useEffect(() => {
    const panel = chatScrollRef.current;
    if (!panel) return;
    const top = panel.scrollHeight;
    if (typeof panel.scrollTo === 'function') {
      panel.scrollTo({ top, behavior: messagesRef.current.length ? 'smooth' : 'auto' });
    } else {
      panel.scrollTop = top;
    }
  }, [messages, loading, error]);

  const submit = async (rawMessage, options = {}) => {
    const text = String(rawMessage ?? input).trim();
    if (!text || inFlightRef.current) return;
    const history = options.history || buildReportsConversationHistory(messagesRef.current);
    if (options.appendUser !== false) {
      replaceMessages([...messagesRef.current, { id: `user-${Date.now()}-${Math.random()}`, role: 'user', content: text, language }]);
    }
    setInput('');
    updateActiveConversation({ draft: '', language, usePageContext });
    setError(null);
    setLoading(true);
    inFlightRef.current = true;
    const requestId = ++requestRef.current;
    const requestedLanguage = language;
    const body = {
      message: text,
      language: requestedLanguage,
      messages: history,
      ...(sessionRef.current ? { session_id: sessionRef.current } : {}),
      ...(usePageContext && cleanContext ? { page_context: cleanContext } : {}),
    };
    try {
      const response = await CommanServices.queryReportsAssistant(body);
      if (requestId !== requestRef.current) return;
      const payload = reportsAssistantPayload(response);
      const presentation = answerPresentation(payload || {});
      const answer = String(
        payload?.answer
        || ((presentation.mode === 'refusal' || presentation.mode === 'unavailable' || presentation.mode === 'clarification')
          ? payload?.detail || payload?.message || presentation.detail
          : ''),
      ).trim();
      if (responseFailed(response) || !answer) {
        throw new Error(payload?.detail || response?.data?.detail || response?.message || 'Reports Copilot could not produce an answer.');
      }
      const nextSessionId = payload?.session_id || payload?.sessionId || sessionRef.current;
      sessionRef.current = nextSessionId || '';
      setSessionId(nextSessionId || '');
      updateActiveConversation({ sessionId: nextSessionId || '' });
      replaceMessages([...messagesRef.current, {
        id: `assistant-${Date.now()}-${Math.random()}`,
        role: 'assistant',
        content: answer,
        payload,
        language: requestedLanguage,
      }]);
      if (nextSessionId && typeof CommanServices.updateReportsAssistantConversation === 'function') {
        const currentConversation = conversationsRef.current.find((conversation) => (
          conversation.id === activeConversationIdRef.current
        ));
        const firstQuestion = messagesRef.current.find((message) => message.role === 'user')?.content;
        const title = currentConversation?.title === 'New conversation'
          ? automaticConversationTitle(firstQuestion)
          : currentConversation?.title;
        if (title) {
          CommanServices.updateReportsAssistantConversation(nextSessionId, { title }).catch(() => {
            if (mountedRef.current) setHistoryStatus('unavailable');
          });
        }
      }
    } catch (requestError) {
      if (requestId === requestRef.current) {
        setError({
          message: responseError(requestError, 'Reports Copilot is temporarily unavailable.'),
          retryMessage: text,
          history,
        });
      }
    } finally {
      if (requestId === requestRef.current) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  const activateConversation = (conversation, { focusComposer = false } = {}) => {
    requestRef.current += 1;
    inFlightRef.current = false;
    activeConversationIdRef.current = conversation.id;
    setActiveConversationId(conversation.id);
    messagesRef.current = conversation.messages || [];
    setMessages(conversation.messages || []);
    sessionRef.current = conversation.sessionId || '';
    setSessionId(conversation.sessionId || '');
    setInput(conversation.draft || '');
    setLanguage(conversation.language || 'English');
    setUsePageContext(Boolean(conversation.usePageContext && cleanContext));
    setError(null);
    setLoading(false);
    setCopiedId('');
    setRenamingConversationId('');
    setRenameDraft('');
    setDeleteConfirmId('');
    setSidebarOpen(false);
    const panel = chatScrollRef.current;
    if (panel) {
      if (typeof panel.scrollTo === 'function') panel.scrollTo({ top: 0, behavior: 'auto' });
      else panel.scrollTop = 0;
    }
    if (focusComposer) window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
  };

  const loadConversationDetail = async (conversation) => {
    if (
      !conversation?.sessionId
      || conversation.messagesLoaded
      || typeof CommanServices.getReportsAssistantConversation !== 'function'
    ) return conversation;
    try {
      const response = await CommanServices.getReportsAssistantConversation(conversation.sessionId);
      const record = reportsConversationRecord(response);
      const hydrated = normalizeServerConversation(record, conversation);
      if (!hydrated) return conversation;
      commitConversations((current) => current.map((item) => (
        item.id === conversation.id ? hydrated : item
      )));
      setHistoryStatus('synced');
      return hydrated;
    } catch (_historyError) {
      if (mountedRef.current) setHistoryStatus('unavailable');
      return conversation;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadSavedConversations = async () => {
      if (typeof CommanServices.listReportsAssistantConversations !== 'function') {
        if (!cancelled) setHistoryStatus('unavailable');
        return;
      }
      try {
        const response = await CommanServices.listReportsAssistantConversations();
        if (cancelled || responseFailed(response)) throw new Error('Saved conversation history is unavailable.');
        const saved = reportsConversationList(response)
          .map((conversation) => normalizeServerConversation(conversation, {
            language: initialUiRef.current.language,
            usePageContext: Boolean(initialUiRef.current.usePageContext && cleanContext),
          }))
          .filter(Boolean)
          .sort((left, right) => right.updatedAt - left.updatedAt);
        if (cancelled) return;
        const active = conversationsRef.current.find((conversation) => (
          conversation.id === activeConversationIdRef.current
        ));
        const untouched = (
          conversationsRef.current.length === 1
          && messagesRef.current.length === 0
          && !active?.draft
        );
        if (!untouched) {
          setHistoryStatus('synced');
          return;
        }
        if (!saved.length) {
          setHistoryStatus('synced');
          return;
        }
        let selected = saved[0];
        if (!selected.messagesLoaded && typeof CommanServices.getReportsAssistantConversation === 'function') {
          try {
            const detailResponse = await CommanServices.getReportsAssistantConversation(selected.sessionId);
            selected = normalizeServerConversation(reportsConversationRecord(detailResponse), selected) || selected;
          } catch (_detailError) {
            // The list still remains useful if one detail request is temporarily unavailable.
          }
        }
        if (cancelled) return;
        const next = saved.map((conversation) => (
          conversation.id === selected.id ? selected : conversation
        ));
        commitConversations(next);
        activateConversation(selected);
        setHistoryStatus('synced');
      } catch (_historyError) {
        if (!cancelled) setHistoryStatus('unavailable');
      }
    };
    loadSavedConversations();
    return () => { cancelled = true; };
    // Conversation hydration intentionally runs only once for each mounted workspace.
  }, []);

  const newConversation = () => {
    const conversation = createConversationRecord({ language, usePageContext });
    commitConversations((current) => [
      conversation,
      ...current.filter((item) => item.messages.length > 0 || item.messageCount > 0),
    ]);
    setConversationSearch('');
    activateConversation(conversation, { focusComposer: true });
  };

  const selectConversation = async (conversationId) => {
    const conversation = conversationsRef.current.find((item) => item.id === conversationId);
    if (!conversation || conversation.id === activeConversationIdRef.current) {
      setSidebarOpen(false);
      return;
    }
    const hydrated = await loadConversationDetail(conversation);
    if (mountedRef.current) activateConversation(hydrated);
  };

  const startRenamingConversation = (conversation) => {
    setDeleteConfirmId('');
    setRenamingConversationId(conversation.id);
    setRenameDraft(conversation.title);
  };

  const commitConversationRename = (conversationId) => {
    const title = automaticConversationTitle(renameDraft);
    const target = conversationsRef.current.find((conversation) => conversation.id === conversationId);
    commitConversations((current) => current.map((conversation) => (
      conversation.id === conversationId
        ? { ...conversation, title, updatedAt: Date.now() }
        : conversation
    )));
    setRenamingConversationId('');
    setRenameDraft('');
    if (target?.sessionId && typeof CommanServices.updateReportsAssistantConversation === 'function') {
      CommanServices.updateReportsAssistantConversation(target.sessionId, { title }).catch(() => {
        if (!mountedRef.current) return;
        setHistoryStatus('unavailable');
        commitConversations((current) => current.map((conversation) => (
          conversation.id === conversationId
            ? { ...conversation, title: target.title, updatedAt: target.updatedAt }
            : conversation
        )));
      });
    }
  };

  const deleteConversation = (conversationId) => {
    const target = conversationsRef.current.find((conversation) => conversation.id === conversationId);
    let remaining = conversationsRef.current.filter((conversation) => conversation.id !== conversationId);
    const deletingActive = conversationId === activeConversationIdRef.current;
    if (target?.sessionId && typeof CommanServices.deleteReportsAssistantConversation === 'function') {
      CommanServices.deleteReportsAssistantConversation(target.sessionId).catch(() => {
        if (mountedRef.current) setHistoryStatus('unavailable');
      });
    }
    if (!deletingActive) {
      commitConversations(remaining);
      setDeleteConfirmId('');
      return;
    }
    if (remaining.length === 0) {
      remaining = [createConversationRecord({ language, usePageContext })];
    }
    remaining = [...remaining].sort((left, right) => right.updatedAt - left.updatedAt);
    commitConversations(remaining);
    activateConversation(remaining[0]);
  };

  const changeLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    updateActiveConversation({ language: nextLanguage });
  };

  const changePageContextScope = (nextValue) => {
    setUsePageContext(nextValue);
    updateActiveConversation({ usePageContext: nextValue });
  };

  const changeInput = (value) => {
    setInput(value);
    updateActiveConversation({ draft: value });
  };

  const copyMessage = async (message) => {
    try {
      await navigator.clipboard?.writeText?.(message.content);
      setCopiedId(message.id);
      window.setTimeout(() => setCopiedId(''), 1500);
    } catch (_error) {
      setCopiedId('');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const fillComposer = (question) => {
    changeInput(question);
    inputRef.current?.focus({ preventScroll: true });
  };

  const hasEmbeddedConversation = messages.length > 0 || loading || Boolean(error);
  const startedConversations = useMemo(() => (
    conversations
      .filter((conversation) => conversation.messages.length > 0 || conversation.messageCount > 0)
      .sort((left, right) => right.updatedAt - left.updatedAt)
  ), [conversations]);
  const normalizedConversationSearch = conversationSearch.trim().toLowerCase();
  const visibleConversations = useMemo(() => {
    if (!normalizedConversationSearch) return startedConversations;
    return startedConversations.filter((conversation) => {
      const userText = conversation.messages
        .filter((message) => message.role === 'user')
        .map((message) => message.content)
        .join(' ');
      return `${conversation.title} ${userText}`.toLowerCase().includes(normalizedConversationSearch);
    });
  }, [normalizedConversationSearch, startedConversations]);

  const embeddedLandingPanel = embedded ? (
    <div
      role="region"
      aria-labelledby="reports-copilot-title"
      aria-describedby="reports-copilot-description"
      data-testid="reports-copilot-embedded"
      className="reports-copilot-theme reports-copilot-shell reports-copilot-shell--embedded relative flex h-full min-h-[560px] w-full overflow-hidden bg-[#F8FAFB] text-[#131A33] [font-family:'Poppins','Inter',sans-serif]"
      onClick={(event) => event.stopPropagation()}
    >
      <ReportsConversationSidebar
        open={sidebarOpen}
        collapsed={sidebarCollapsed}
        conversations={visibleConversations}
        allConversationCount={startedConversations.length}
        activeId={activeConversationId}
        search={conversationSearch}
        renamingId={renamingConversationId}
        renameDraft={renameDraft}
        deleteConfirmId={deleteConfirmId}
        historyStatus={historyStatus}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
        onNew={newConversation}
        onSearch={setConversationSearch}
        onSelect={selectConversation}
        onStartRename={startRenamingConversation}
        onRenameDraft={setRenameDraft}
        onCommitRename={commitConversationRename}
        onCancelRename={() => {
          setRenamingConversationId('');
          setRenameDraft('');
        }}
        onRequestDelete={(conversationId) => {
          setRenamingConversationId('');
          setDeleteConfirmId(conversationId);
        }}
        onCancelDelete={() => setDeleteConfirmId('')}
        onConfirmDelete={deleteConversation}
      />
      <section className="reports-copilot-main flex min-w-0 flex-1 flex-col">
      <div className="reports-copilot-main-inner flex h-full min-h-0 w-full flex-1 flex-col px-4 pb-5 pt-5 sm:px-6 lg:px-8">
        <p id="reports-copilot-description" className="sr-only">Ask questions across supported authorized incident and operational reporting data</p>

        <div className="grid flex-none grid-cols-[1fr_auto] items-start gap-2.5 lg:grid-cols-[1fr_auto_1fr]">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open conversation history"
            className="reports-copilot-ghost flex h-10 w-10 flex-none items-center justify-center rounded-[11px] border border-[#E2E4EE] bg-white text-[#4B5468] transition hover:border-[#C7CBEA] hover:text-[#131A33] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/30 lg:hidden"
            data-testid="reports-copilot-sidebar-open"
          >
            <PanelLeftOpen className="h-[17px] w-[17px]" strokeWidth={1.9} />
          </button>

          <div
            className="reports-copilot-scope-control col-span-2 row-start-2 inline-flex min-w-0 justify-self-center rounded-[10px] border border-[#EAECF3] bg-white p-1 shadow-[0_1px_2px_rgba(20,26,51,0.04)] lg:col-span-1 lg:col-start-2 lg:row-start-1"
            data-testid="reports-copilot-context-bar"
            role="group"
            aria-label="Data scope"
          >
            <button
              type="button"
              aria-pressed={!usePageContext}
              onClick={() => changePageContextScope(false)}
              title="Search supported incident and operational reporting records you are authorized to view"
              className={`inline-flex h-8 items-center gap-2 rounded-[8px] px-3 text-[11px] font-semibold transition sm:px-3.5 sm:text-[12.5px] ${!usePageContext
                ? 'bg-[#F1F2F8] text-[#131A33]'
                : 'text-[#8A90A6] hover:text-[#4B5468]'}`}
              data-testid="reports-copilot-context-all"
            >
              <span className="h-[7px] w-[7px] rounded-full bg-[#10B981] shadow-[0_0_0_3px_rgba(16,185,129,0.18)]" />
              All authorized data
            </button>
            <button
              type="button"
              aria-pressed={usePageContext}
              disabled={!cleanContext}
              onClick={() => changePageContextScope(true)}
              title={cleanContext
                ? `Limit the answer to the current Reports dashboard scope and filters · ${contextSummary}`
                : 'No Reports dashboard context is currently available'}
              className={`h-8 rounded-[8px] px-3 text-[11px] font-semibold transition sm:px-3.5 sm:text-[12.5px] ${usePageContext
                ? 'bg-[#F1F2F8] text-[#131A33]'
                : 'text-[#8A90A6] hover:text-[#4B5468] disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:text-[#8A90A6]'}`}
              data-testid={usePageContext ? 'reports-copilot-context-active' : 'reports-copilot-context-attach'}
            >
              Use current page context
            </button>
          </div>

          <div className="col-start-2 row-start-1 flex items-center justify-end gap-2.5 lg:col-start-3">
            <ReportsLanguageSelect value={language} onValueChange={changeLanguage} variant="landing" />
            <button
              type="button"
              onClick={onClose}
              aria-label="Open Reports dashboard"
              title="Reports dashboard"
              className="reports-copilot-primary flex h-10 w-10 items-center justify-center rounded-[11px] bg-[#0B1220] text-white transition hover:bg-[#1B2437] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/30 focus-visible:ring-offset-2"
              data-testid="reports-copilot-dashboard"
            >
              <LayoutDashboard className="h-[17px] w-[17px]" strokeWidth={2.1} />
            </button>
          </div>
        </div>

        {usePageContext && cleanContext && (
          <div
            className="reports-copilot-context-summary mx-auto mt-2.5 flex w-full max-w-[1040px] flex-wrap items-center gap-1.5 rounded-[11px] border border-[#DDE1EA] bg-white/90 px-2.5 py-1.5 shadow-[0_8px_22px_-18px_rgba(19,26,51,0.45)]"
            role="status"
            aria-label={`Current page context: ${contextSummary}`}
            data-testid="reports-copilot-context-summary"
          >
            <span className="mr-0.5 inline-flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-[#7B8296]">
              <Database className="h-3.5 w-3.5 text-[#4F46E5]" strokeWidth={1.9} />
              Answer limited to
            </span>
            {pageContextDetails.map((detail) => (
              <span
                key={detail.key}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#E2E4EE] bg-[#F8FAFB] px-2.5 py-1 text-[10.5px] text-[#4B5468]"
                data-testid={`reports-copilot-context-chip-${detail.key}`}
                title={`${detail.label}: ${detail.value}`}
              >
                <span className="font-bold text-[#7B8296]">{detail.label}</span>
                <span className="max-w-[190px] truncate font-semibold text-[#131A33]">{detail.value}</span>
              </span>
            ))}
            {!hasPageContextFilters && (
              <span className="inline-flex items-center rounded-full border border-dashed border-[#DDE1EA] px-2.5 py-1 text-[10.5px] font-medium text-[#8A90A6]" data-testid="reports-copilot-context-no-filters">
                No additional filters
              </span>
            )}
            <button
              type="button"
              onClick={() => changePageContextScope(false)}
              className="ml-auto inline-flex h-7 items-center gap-1 rounded-[8px] px-2 text-[10px] font-bold text-[#4B5468] transition hover:bg-[#F1F2F8] hover:text-[#131A33] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/30"
              aria-label="Remove current page context and use all authorized data"
              data-testid="reports-copilot-context-clear"
            >
              <X className="h-3.5 w-3.5" />
              Back to all
            </button>
          </div>
        )}

        <main
          ref={chatScrollRef}
          className="reports-copilot-chat flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain py-3 lg:py-4"
          dir={isArabic ? 'rtl' : 'ltr'}
          aria-live="polite"
          aria-busy={loading}
          data-testid="reports-copilot-chat-scroll"
        >
          {!hasEmbeddedConversation ? (
            <div className="reports-copilot-landing m-auto w-full max-w-[1040px] py-3" data-testid="reports-copilot-landing">
              <div className="reports-copilot-landing-column mx-auto w-full max-w-[720px]">
                <div className="reports-copilot-landing-copy mb-4 text-center">
                  <h2
                    id="reports-copilot-title"
                    className="text-center text-[26px] font-bold leading-[1.1] tracking-[-0.025em] text-[#131A33] sm:text-[30px] lg:text-[34px]"
                    data-testid="reports-copilot-welcome"
                  >
                    What would you like to know?
                  </h2>
                  <p className="mx-auto mt-2 max-w-[720px] text-[12px] leading-[1.55] text-[#6B7280] sm:text-[13px]">
                    Ask about supported authorized incident and operational reporting data, numbers, status, or trends.
                  </p>
                </div>

                <div
                  className="reports-copilot-suggestions w-full divide-y divide-[#F0F1F6] overflow-hidden rounded-[12px] border border-[#EAECF3] bg-white shadow-[0_10px_30px_-22px_rgba(20,26,51,0.4)]"
                  dir="ltr"
                  aria-label="Suggested Reports Copilot questions"
                  data-layout="compact-list"
                  data-testid="reports-copilot-suggestions"
                >
                  {EXAMPLE_QUESTIONS.map(({ question, label, icon: ExampleIcon }, index) => (
                    <button
                      key={question}
                      type="button"
                      onClick={() => fillComposer(question)}
                      aria-label={`Fill the Reports Copilot composer with: ${question}`}
                      className="reports-copilot-suggestion-row reports-copilot-suggestion-row--neutral group flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition hover:bg-[#FAFAFE] focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4F46E5]/35 sm:px-4"
                      data-tone="neutral"
                      data-testid={`reports-copilot-example-${index}`}
                    >
                      <span className="reports-copilot-suggestion-icon reports-copilot-suggestion-icon--neutral inline-flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[8px]">
                        <ExampleIcon className="h-[15px] w-[15px]" strokeWidth={1.9} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className="reports-copilot-suggestion-category block text-[8.5px] font-bold uppercase tracking-[0.12em]"
                          data-testid={`reports-copilot-example-${index}-category`}
                        >
                          {label}
                        </span>
                        <span className="mt-0.5 block text-[12.5px] font-semibold leading-[1.35] text-[#131A33] sm:text-[13px]" data-testid={`reports-copilot-example-${index}-question`}>
                          {question}
                        </span>
                      </span>
                      <span className="inline-flex flex-none text-[#C3C8D8] transition group-hover:text-[#8A90A6]">
                        <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transform-none" strokeWidth={2.1} />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="reports-copilot-conversation mx-auto flex w-full max-w-[1040px] flex-col gap-4 py-3" data-testid="reports-copilot-conversation">
              <h2 id="reports-copilot-title" className="sr-only">Reports Copilot</h2>
              {messages.map((message) => message.role === 'user' ? (
                <div
                  key={message.id}
                  className="max-w-[86%] self-end rounded-[18px] rounded-tr-sm bg-[#131A33] px-4 py-3 text-sm leading-relaxed text-white shadow-sm"
                  data-testid="reports-copilot-message-user"
                  dir={message.language === 'Arabic' ? 'rtl' : 'ltr'}
                >
                  {message.content}
                </div>
              ) : (
                <AssistantMessage key={message.id} message={message} onCopy={copyMessage} copied={copiedId === message.id} onFollowUp={submit} />
              ))}

              {loading && (
                <div className="flex max-w-[94%] items-center gap-3 self-start rounded-[18px] rounded-tl-sm border border-[#E2E4EE] bg-white px-4 py-3 text-sm text-[#4B5468] shadow-sm" data-testid="reports-copilot-loading">
                  <Loader2 className="h-4 w-4 animate-spin text-[#0EA5A0]" />
                  <span>
                    <strong className="text-[#131A33]">Reviewing authoritative platform data…</strong>
                    <br />
                    <span className="text-xs">Calculating records, timelines, SLAs and report evidence</span>
                  </span>
                </div>
              )}

              {error && !loading && (
                <div className="max-w-[94%] self-start rounded-[18px] border border-red-200 bg-white p-4 text-sm text-red-700 shadow-sm" role="alert" data-testid="reports-copilot-error">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold">Reports Copilot could not complete this question</p>
                      <p className="mt-1 text-xs">{error.message}</p>
                    </div>
                    <button type="button" onClick={() => submit(error.retryMessage, { appendUser: false, history: error.history })} className="inline-flex items-center gap-1 text-xs font-bold hover:underline" data-testid="reports-copilot-retry">
                      <RefreshCw className="h-3.5 w-3.5" />
                      Retry
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>

        <footer className="reports-copilot-footer mx-auto w-full max-w-[1040px] pt-4">
          {!hasEmbeddedConversation && (
            <div className="reports-copilot-try-row mb-3 flex flex-wrap items-center justify-between gap-2" dir="ltr">
              <span className="text-[11px] font-semibold text-[#9CA3AF]">Try:</span>
              {QUICK_PROMPTS.map(({ label, question }, index) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => fillComposer(question)}
                  className="h-8 rounded-full border border-[#E2E4EE] bg-white px-3 text-[12px] font-medium text-[#4B5468] transition hover:border-[#C7CBEA] hover:text-[#131A33] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/30"
                  data-testid={`reports-copilot-quick-prompt-${index}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="reports-copilot-composer flex items-stretch gap-2 rounded-[14px] border-[1.5px] border-[#E2E4EE] bg-white p-2 shadow-[0_22px_50px_-30px_rgba(30,35,75,0.55)] transition focus-within:border-[#4F46E5]">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(event) => changeInput(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              dir={isArabic ? 'rtl' : 'ltr'}
              placeholder={isArabic ? 'اسأل عن بيانات الحوادث والعمليات والتقارير المدعومة…' : 'Ask about supported incidents, operations or reports…'}
              aria-label="Ask Reports Copilot"
              className="reports-copilot-composer-input max-h-28 min-h-[44px] w-full min-w-0 flex-1 resize-none rounded-[8px] border border-transparent bg-transparent px-3 py-[10px] text-[15px] leading-[22px] text-[#131A33] outline-none placeholder:text-[#9AA0B4]"
              data-testid="reports-copilot-input"
            />
            <button
              type="button"
              onClick={() => submit()}
              disabled={loading || !input.trim()}
              aria-label="Send question"
              className={`flex h-11 w-11 flex-none items-center justify-center rounded-[12px] text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed ${input.trim()
                ? 'bg-[#4F46E5] hover:bg-[#4338CA]'
                : 'bg-[#B9BDD6]'}`}
              data-testid="reports-copilot-send"
            >
              <Send className="h-[18px] w-[18px] fill-current" strokeWidth={1.7} />
            </button>
          </div>

          <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[11px] text-[#9CA3AF]">
            <LockKeyhole className="h-3 w-3 flex-none" strokeWidth={1.8} />
            Answers calculated from authorized records, with methodology and linked evidence.
          </p>
          {sessionId && <span className="sr-only" data-testid="reports-copilot-session">Active conversation {sessionId}</span>}
        </footer>
      </div>
      </section>
    </div>
  ) : null;

  const copilotPanel = (
      <div
        role={embedded ? 'region' : 'dialog'}
        aria-modal={embedded ? undefined : 'true'}
        aria-labelledby="reports-copilot-title"
        aria-describedby="reports-copilot-description"
        data-testid={embedded ? 'reports-copilot-embedded' : 'reports-copilot-panel'}
        className={`reports-copilot-theme reports-copilot-shell reports-copilot-shell--dialog flex min-h-0 w-full flex-col overflow-hidden bg-white ${embedded
          ? 'h-full min-h-[560px] rounded-[24px] border border-slate-200/70 shadow-[0_24px_70px_-38px_rgba(15,23,42,0.38)]'
          : 'h-[100dvh] max-w-4xl shadow-2xl sm:h-[88vh] sm:rounded-2xl sm:border sm:border-cyan-200/30'}`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="reports-copilot-header relative flex flex-wrap items-center justify-between gap-4 overflow-hidden border-b border-white/10 bg-transparent px-4 py-4 text-white sm:px-7 sm:py-5">
          <span aria-hidden="true" className="pointer-events-none absolute -left-20 -top-24 h-52 w-52 rounded-full bg-indigo-500/20 blur-3xl" />
          <span aria-hidden="true" className="pointer-events-none absolute -bottom-28 right-8 h-56 w-56 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="relative z-10 flex min-w-0 items-center gap-3.5">
            <ReportsCopilotMark />
            <div className="min-w-0">
              <p className="mb-0.5 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-200/65">AI reporting assistant</p>
              <h2 id="reports-copilot-title" className="truncate text-lg font-black tracking-tight sm:text-xl">Reports Copilot</h2>
              <p id="reports-copilot-description" className="truncate text-xs text-cyan-100/65">Ask questions across supported authorized incident and operational reporting data</p>
            </div>
          </div>
          <div className="relative z-10 flex flex-wrap items-center justify-end gap-2">
            <ReportsLanguageSelect value={language} onValueChange={setLanguage} />
            <button type="button" onClick={newConversation} className="reports-copilot-primary inline-flex h-10 items-center gap-1.5 rounded-xl border border-transparent px-3 text-xs font-bold text-white/75 transition hover:border-white/10 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" data-testid="reports-copilot-new" title="Start a new conversation"><Plus className="h-4 w-4" /><span className="hidden sm:inline">New</span></button>
            {embedded ? (
              <button type="button" onClick={onClose} aria-label="Open Reports dashboard" className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-white/15 bg-white/10 px-3 text-xs font-bold text-white/85 backdrop-blur-md transition hover:border-white/25 hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" data-testid="reports-copilot-dashboard">
                <LayoutDashboard className="h-4 w-4" /><span className="hidden md:inline">Reports dashboard</span>
              </button>
            ) : (
              <button type="button" onClick={onClose} aria-label="Close Reports Copilot" className="flex h-10 w-10 items-center justify-center rounded-xl text-white/70 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" data-testid="reports-copilot-close"><X className="h-5 w-5" /></button>
            )}
          </div>
        </header>

        <div className="reports-copilot-scope-row flex flex-wrap items-center gap-2.5 border-b border-slate-200/80 bg-white/95 px-4 py-3 text-xs shadow-[0_1px_0_rgba(15,23,42,0.02)] sm:px-7" data-testid="reports-copilot-context-bar">
          <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
            <Database className="h-4 w-4" />
          </span>
          <span className="hidden text-[9px] font-black uppercase tracking-[0.16em] text-slate-400 sm:inline">Data scope</span>
          {usePageContext && cleanContext ? (
            <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 py-1.5 pl-3 pr-1.5 text-indigo-800 shadow-sm" data-testid="reports-copilot-context-active" title={contextSummary}>
              <span className="truncate font-semibold">Current page · {contextSummary}</span>
              <button type="button" onClick={() => setUsePageContext(false)} aria-label="Remove current page context" className="rounded-full p-1 transition hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400" data-testid="reports-copilot-context-remove"><X className="h-3 w-3" /></button>
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200/80 bg-emerald-50/80 px-3 py-1.5 font-bold text-emerald-800 shadow-sm" data-testid="reports-copilot-context-all">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" />All authorized data
            </span>
          )}
          {!usePageContext && cleanContext && (
            <button type="button" onClick={() => setUsePageContext(true)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-bold text-indigo-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300" data-testid="reports-copilot-context-attach">Use current page context</button>
          )}
          <span className="ml-auto hidden items-center gap-1.5 text-[10px] text-slate-400 lg:inline-flex"><ShieldCheck className="h-3.5 w-3.5 text-slate-400" />Page context is an optional hint, not a data source.</span>
        </div>

        <main ref={chatScrollRef} className="reports-copilot-chat flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain bg-transparent px-4 py-5 sm:px-7" aria-live="polite" aria-busy={loading} dir={isArabic ? 'rtl' : 'ltr'} data-testid="reports-copilot-chat-scroll">
          {messages.length === 0 && !loading && !error && (
            <div className="m-auto w-full max-w-5xl py-6 text-center sm:py-9" data-testid="reports-copilot-welcome">
              <div className="relative inline-flex">
                <span aria-hidden="true" className="absolute inset-1 rounded-3xl bg-indigo-400/20 blur-2xl" />
                <ReportsCopilotMark size="lg" className="relative" />
              </div>
              <div className="mx-auto mt-5 inline-flex items-center gap-1.5 rounded-full border border-indigo-100 bg-white/80 px-3 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-indigo-700 shadow-sm backdrop-blur">
                <Sparkles className="h-3 w-3" />Evidence-backed operational intelligence
              </div>
              <h3 className="mt-4 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">What would you like to know?</h3>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">Ask naturally about supported incident and operational reporting data. Reports Copilot calculates from the latest records you are authorized to see.</p>
              <div className="reports-copilot-suggestion-cards mt-7 grid gap-3 text-left md:grid-cols-3" dir="ltr">
                {EXAMPLE_QUESTIONS.map(({ question, label, icon: ExampleIcon, iconClass, themeClass, accentClass }, index) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => fillComposer(question)}
                    aria-label={`Fill the Reports Copilot composer with: ${question}`}
                    className="reports-copilot-suggestion-card group relative flex min-h-[172px] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-[0_12px_35px_-24px_rgba(15,23,42,0.34)] backdrop-blur transition duration-300 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-[0_20px_44px_-24px_rgba(79,70,229,0.34)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 motion-reduce:transform-none sm:p-5"
                    data-testid={`reports-copilot-example-${index}`}
                  >
                    <span aria-hidden="true" className={`pointer-events-none absolute inset-0 bg-transparent ${accentClass}`} />
                    <span className="relative flex items-start justify-between gap-3">
                      <span className={`reports-copilot-suggestion-icon ${themeClass} inline-flex h-9 w-9 items-center justify-center rounded-xl ring-1 ${iconClass}`}><ExampleIcon className="h-4 w-4" /></span>
                      <ArrowUpRight className="h-4 w-4 text-slate-300 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-indigo-500 motion-reduce:transform-none" />
                    </span>
                    <span className="relative mt-4 block text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">{label}</span>
                    <span className="relative mt-2 block text-sm font-bold leading-5 text-slate-800">{question}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.length > 0 && (
            <div className="flex w-full flex-col gap-4">
              {messages.map((message) => message.role === 'user' ? (
                <div key={message.id} className="max-w-[86%] self-end rounded-2xl rounded-tr-sm bg-indigo-700 px-4 py-3 text-sm leading-relaxed text-white shadow-sm" data-testid="reports-copilot-message-user" dir={message.language === 'Arabic' ? 'rtl' : 'ltr'}>{message.content}</div>
              ) : (
                <AssistantMessage key={message.id} message={message} onCopy={copyMessage} copied={copiedId === message.id} onFollowUp={submit} />
              ))}
            </div>
          )}

          {loading && (
            <div className="mt-4 flex max-w-[94%] items-center gap-3 self-start rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm" data-testid="reports-copilot-loading">
              <Loader2 className="h-4 w-4 animate-spin text-cyan-700" /><span><strong className="text-slate-800">Reviewing authoritative platform data…</strong><br /><span className="text-xs">Calculating records, timelines, SLAs and report evidence</span></span>
            </div>
          )}

          {error && !loading && (
            <div className="mt-4 max-w-[94%] self-start rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert" data-testid="reports-copilot-error">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                <div className="min-w-0 flex-1"><p className="font-bold">Reports Copilot could not complete this question</p><p className="mt-1 text-xs">{error.message}</p></div>
                <button type="button" onClick={() => submit(error.retryMessage, { appendUser: false, history: error.history })} className="inline-flex items-center gap-1 text-xs font-bold hover:underline" data-testid="reports-copilot-retry"><RefreshCw className="h-3.5 w-3.5" />Retry</button>
              </div>
            </div>
          )}
        </main>

        <footer className="reports-copilot-footer border-t border-slate-200/80 bg-white/95 px-3 py-3.5 backdrop-blur sm:px-7">
          <div className="mx-auto max-w-[1040px]">
            <div className="reports-copilot-composer flex items-end gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_14px_34px_-22px_rgba(15,23,42,0.42)] transition focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-100/80">
              <span className="mb-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><Sparkles className="h-4 w-4" /></span>
              <textarea ref={inputRef} value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={handleKeyDown} rows={1} dir={isArabic ? 'rtl' : 'ltr'} placeholder={isArabic ? 'اسأل عن بيانات الحوادث والعمليات والتقارير المدعومة…' : 'Ask about supported incidents, operations or reports…'} aria-label="Ask Reports Copilot" className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent px-1 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400" data-testid="reports-copilot-input" />
              <button type="button" onClick={() => submit()} disabled={loading || !input.trim()} aria-label="Send question" className="reports-copilot-primary flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white text-[#1e3a8a] shadow-[0_8px_20px_-8px_rgba(79,70,229,0.7)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40" data-testid="reports-copilot-send"><Send className="h-4 w-4" /></button>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] text-slate-400">
              <span>Enter to send · Shift+Enter for a new line</span>
              <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline-block" />
              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Answers calculated from authorized records, with methodology and linked evidence.</span>
            </div>
          </div>
          {sessionId && <span className="sr-only" data-testid="reports-copilot-session">Active conversation {sessionId}</span>}
        </footer>
      </div>
  );

  if (embedded) return embeddedLandingPanel || copilotPanel;

  return (
    <div className="fixed inset-0 z-[1130] flex items-center justify-center bg-[#0f172a] p-0 sm:p-5" data-testid="reports-copilot-modal" onClick={onClose}>
      {copilotPanel}
    </div>
  );
};

export default ReportsCopilotModal;
